// Comprehensive Delaware family law data based on FindLaw and official Delaware sources
const delawareData = {
  'delaware': {
    name: 'Delaware',
    overview: {
      keyPoints: [
        'Delaware courts determine custody based on the "best interests of the child" standard outlined in 13 Del. C. § 722',
        'The state uses the unique "Melson Formula" for calculating child support, which is distinct from the Income Shares Model used by most other states',
        'Mediation is mandatory in almost all custody and visitation cases before a judge will hear the matter',
        'Delaware law distinguishes between "Legal Custody" (decision-making authority) and "Residential Placement" (physical living arrangements)',
        'Parents involved in divorce or custody proceedings are required to complete a certified parenting education class',
        'The Delaware Family Court is a unified court system that handles all family law matters statewide'
      ],
      uniqueFeatures: [
        'The "Melson Formula" ensures parents\' basic needs are met (Self-Support Reserve) before calculating primary support for the child',
        'Delaware has specific "Standard Contact Guidelines" often used as a baseline for visitation schedules in each county',
        'The state allows for "ex parte" (emergency) custody orders only upon a showing of immediate and irreparable harm',
        'Delaware courts have a strong preference for joint legal custody regardless of the residential arrangement'
      ]
    },
    legalRequirements: {
      residency: 'To file for divorce, one spouse must have resided in Delaware for at least 6 months immediately prior to filing. For custody, Delaware must generally be the child\'s "home state" (6 months residency).',
      filing: 'File a Petition for Custody or Petition for Divorce in the Family Court of the county where the respondent resides or where the child resides',
      paternity: 'Established via Voluntary Acknowledgment of Paternity (VAP) at birth, genetic testing, or court adjudication',
      documentation: 'Petition form, Information Sheet, and Affidavit of Parentage (if applicable). Financial reporting forms are required for support cases.',
      uniqueRules: [
        'Mandatory mediation is the first step in contested custody cases; parties meet with a mediator to attempt an agreement before seeing a judge',
        'A "Parenting Education Class" certificate must be filed with the court',
        'Children generally do not testify in court; their preferences may be presented through a Guardian ad Litem or interview'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Referred to as "Residential Placement"; can be Primary (child lives mostly with one parent) or Shared (50/50 or close to it)',
      legalCustody: 'Refers to the right to make major decisions (medical, educational, religious); "Joint Legal Custody" is the standard presumption',
      jointCustody: 'Typically means Joint Legal Custody; parents share decision-making but residential placement may vary',
      visitation: 'Referred to as "Visitation" or "Contact"; Delaware counties often use standard visitation guidelines (e.g., alternating weekends, mid-week dinner)',
      specialProvisions: [
        'Third-party visitation (grandparents) is permitted under 13 Del. C. § 2410 if it is in the child\'s best interest',
        'The court may appoint a Court Appointed Special Advocate (CASA) or Guardian ad Litem to represent the child\'s interests',
        'Relocation requires a modification of the custody order and analysis of the "best interests" factors concerning the move'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Residential schedule (weekdays, weekends, overnights)',
        'Holiday and vacation schedule (usually alternating years)',
        'Transportation arrangements and exchange locations (e.g., "curbside" or neutral location)',
        'Decision-making authority for education, medical care, and religious upbringing',
        'Communication guidelines (telephone access, video calls)',
        'Right of first refusal for childcare'
      ],
      tips: [
        'Prepare for mediation seriously; most Delaware custody cases are settled here without a judge',
        'Be familiar with the "Standard Contact Guidelines" for your specific county (New Castle, Kent, Sussex) as a starting point',
        'Clearly specify how "school choice" decisions will be made if parents live in different districts',
        'Include provisions for communicating about medical appointments and school events'
      ]
    },
    childSupport: {
      guidelines: 'Delaware uses the "Melson Formula," which creates a hierarchy of needs: parents\' self-support -> children\'s primary support -> standard of living adjustment (SOLA)',
      factors: [
        'Net income of both parents',
        'Number of children',
        'Health insurance costs',
        'Childcare expenses',
        'Parenting time (number of overnights can trigger a shared custody calculation)',
        'Other support obligations'
      ],
      calculation: 'Calculated using the Delaware Child Support Calculator based on the Melson Formula',
      modifications: 'Can be modified if 2.5 years have passed (review), or if there is a substantial change in circumstances (e.g., job loss, change in custody)',
      enforcement: 'Enforced by the Division of Child Support Services (DCSS) via wage attachment, license suspension, and tax refund intercept'
    },
    resources: {
      courts: [
        {
          name: 'Delaware Family Court',
          url: 'https://courts.delaware.gov/family/',
          description: 'Official website with forms, mediation information, and court calendars'
        },
        {
          name: 'Delaware Child Support Calculator',
          url: 'https://courts.delaware.gov/family/support/calculator.aspx',
          description: 'Official online calculator using the Melson Formula'
        },
        {
          name: 'Community Legal Aid Society, Inc. (CLASI)',
          url: 'http://www.declasi.org/',
          description: 'Legal assistance for victims of domestic violence and eligible low-income residents'
        }
      ],
      support: [
        {
          name: 'Division of Child Support Services (DCSS)',
          url: 'https://dhss.delaware.gov/dhss/dcss/',
          description: 'State agency for enforcing and collecting child support'
        },
        {
          name: 'Delaware Volunteer Legal Services',
          url: 'https://www.dvls.org/',
          description: 'Pro bono legal representation for indigent clients in family law cases'
        },
        {
          name: 'Delaware Coalition Against Domestic Violence',
          url: 'https://dcadv.org/',
          description: 'Resources and shelter information for victims'
        }
      ],
      legal: [
        {
          name: 'Delaware State Bar Association - Find a Lawyer',
          url: 'https://www.dsba.org/online-lawyer-referral-service/',
          description: 'Directory of licensed attorneys in Delaware'
        },
        {
          name: 'Family Court Forms',
          url: 'https://courts.delaware.gov/forms/family.aspx',
          description: 'Downloadable petitions and answer forms'
        }
      ],
      educational: [
        {
          name: 'Parenting Education Class List',
          url: 'https://courts.delaware.gov/family/parenting/',
          description: 'List of court-approved providers for the mandatory parenting course'
        },
        {
          name: 'A Guide to Family Court',
          url: 'https://courts.delaware.gov/family/help/guide.aspx',
          description: 'Official handbook for navigating the Delaware Family Court system'
        }
      ]
    },
    bestInterestsFactors: [
      'The wishes of the child\'s parent or parents as to his or her custody and residential arrangements',
      'The wishes of the child as to his or her custodian and residential arrangements',
      'The interaction and interrelationship of the child with his or her parents, grandparents, siblings, persons cohabiting in the relationship of husband and wife with a parent of the child, any other residents of the household or persons who may significantly affect the child\'s best interests',
      'The child\'s adjustment to his or her home, school and community',
      'The mental and physical health of all individuals involved',
      'Past and present compliance by both parents with their rights and responsibilities to their child under § 701 of this title',
      'Evidence of domestic violence as provided for in Chapter 7A of this title',
      'The criminal history of any party or any other resident of the household including whether the criminal history contains pleas of guilty or no contest or a conviction of a criminal offense'
    ]
  }
};

export default delawareData;